import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Popover } from 'antd-mobile';
import ProgressBar from './ProgressBar';
import CountdownTimer from './CountdownTimer';
import logo3 from '@/assets/images/ai/logo3.png';
import a1 from '@/assets/images/ai/a1.png';
import a2 from '@/assets/images/ai/a2.png';
import err1 from '@/assets/images/ai/err1.png';
import { useI18n } from '@/hooks/useI18n';
import { getSessionDetailById } from '@/lib/api/conversations';
import { getServerTime } from '@/lib/api/system';
import { useParams } from 'react-router-dom';

interface TopStatusBarProps {
  aiIconPosition?: number;
  showFinishBtn?: boolean;
  showTimer?: boolean;
  timerText?: string;
  onFinishClick?: () => void;
  analysisReason?: string;
  showAnalysisBubble?: boolean;
  onToggleAnalysisBubble?: () => void;
  // 时长检查相关
  chatDuration?: number;
  minDuration?: number;
  maxDuration?: number;
  isMinDurationMet?: boolean;
  onTimeUp?: () => void;
}

const TopStatusBar: React.FC<TopStatusBarProps> = ({
  aiIconPosition = 40,
  showFinishBtn = true,
  showTimer = false,
  timerText = '00:00',
  onFinishClick,
  analysisReason = '',
  showAnalysisBubble = false,
  onToggleAnalysisBubble,
  // 时长检查相关
  chatDuration = 0,
  minDuration = 180,
  maxDuration = 0,
  isMinDurationMet = false,
  onTimeUp
}) => {
  const { t } = useI18n();
  const { sessionId } = useParams<{ sessionId: string }>();
  const [practiceTime, setPracticeTime] = useState<string>('00:00');
  const [elapsedSeconds, setElapsedSeconds] = useState<number>(0);
  const [sessionStartTime, setSessionStartTime] = useState<string | null>(null);
  const baseTimeRef = useRef<number>(0);
  const lastCalibrationTimeRef = useRef<number>(Date.now());
  const isTimeUpTriggeredRef = useRef<boolean>(false);
  const onTimeUpRef = useRef(onTimeUp);
  // 时间偏移量：服务器时间 - 本地时间（毫秒）
  const timeOffsetRef = useRef<number>(0);

  // 更新 onTimeUpRef
  useEffect(() => {
    onTimeUpRef.current = onTimeUp;
  }, [onTimeUp]);

  const calculatePracticeTime = useCallback(() => {
    if (!sessionStartTime) return { timeString: '00:00', totalSeconds: 0 };
    
    const startTime = new Date(sessionStartTime);
    // 使用本地时间 + 时间偏移量来获取校准后的当前时间
    const currentTime = new Date(Date.now() + timeOffsetRef.current);
    const diffInSeconds = Math.floor((currentTime.getTime() - startTime.getTime()) / 1000);
    
    const minutes = Math.floor(diffInSeconds / 60);
    const seconds = diffInSeconds % 60;
    
    return {
      timeString: `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`,
      totalSeconds: diffInSeconds
    };
  }, [sessionStartTime]);

  const formatTime = useCallback((totalSeconds: number) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }, []);

  // 获取服务器时间并计算时间偏移量
  useEffect(() => {
    const syncServerTime = async () => {
      try {
        const localTimeBeforeRequest = Date.now();
        const response = await getServerTime();
        const localTimeAfterRequest = Date.now();
        
        if (response.success && response.data) {
          // 估算网络延迟（往返时间的一半）
          const networkDelay = (localTimeAfterRequest - localTimeBeforeRequest) / 2;
          // 服务器时间（毫秒）
          const serverTime = response.data.timestamp * 1000;
          // 估算的服务器当前时间 = 响应中的服务器时间 + 网络延迟
          const estimatedServerTime = serverTime + networkDelay;
          // 计算时间偏移量：服务器时间 - 本地时间
          timeOffsetRef.current = estimatedServerTime - localTimeAfterRequest;
          
          console.log('时间同步成功:', {
            本地时间: new Date(localTimeAfterRequest).toISOString(),
            服务器时间: new Date(serverTime).toISOString(),
            时间偏移: Math.round(timeOffsetRef.current / 1000) + '秒',
            网络延迟: Math.round(networkDelay) + 'ms'
          });
        }
      } catch (error) {
        console.error('同步服务器时间失败:', error);
        // 如果同步失败，偏移量保持为0，使用本地时间
      }
    };

    syncServerTime();
  }, []);

  // 获取会话详情
  useEffect(() => {
    const fetchSessionDetail = async () => {
      if (!sessionId) return;
      
      try {
        const response = await getSessionDetailById(sessionId);
        if (response.success && response.data?.created_at) {
          setSessionStartTime(response.data.created_at);
        }
      } catch (error) {
        console.error('获取会话详情失败:', error);
      }
    };

    fetchSessionDetail();
  }, [sessionId]);

  useEffect(() => {
    if (sessionStartTime) {
      // 立即计算并设置初始时间
      const initialTime = calculatePracticeTime();
      setPracticeTime(initialTime.timeString);
      setElapsedSeconds(initialTime.totalSeconds);
      baseTimeRef.current = initialTime.totalSeconds;
      lastCalibrationTimeRef.current = Date.now();
      
      // 每秒更新显示时间
      const displayInterval = setInterval(() => {
        const now = Date.now();
        const elapsedSinceCalibration = Math.floor((now - lastCalibrationTimeRef.current) / 1000);
        const currentTotalSeconds = baseTimeRef.current + elapsedSinceCalibration;
        
        // 显示逻辑：如果有最大时长，且超过最大时长，则显示最大时长
        let displaySeconds = currentTotalSeconds;
        if (maxDuration > 0 && currentTotalSeconds > maxDuration) {
          displaySeconds = maxDuration;
        }
        
        setPracticeTime(formatTime(displaySeconds));
        setElapsedSeconds(displaySeconds);

        // 检查是否达到最大时长
        if (maxDuration > 0 && currentTotalSeconds >= maxDuration && !isTimeUpTriggeredRef.current) {
          isTimeUpTriggeredRef.current = true;
          if (onTimeUpRef.current) {
            onTimeUpRef.current();
          }
        }
      }, 1000);
      
      // 每30秒校准一次
      const calibrationInterval = setInterval(() => {
        const calibratedTime = calculatePracticeTime();
        baseTimeRef.current = calibratedTime.totalSeconds;
        lastCalibrationTimeRef.current = Date.now();
        
        const currentTotalSeconds = calibratedTime.totalSeconds;
        
        // 显示逻辑：如果有最大时长，且超过最大时长，则显示最大时长
        let displaySeconds = currentTotalSeconds;
        if (maxDuration > 0 && currentTotalSeconds > maxDuration) {
          displaySeconds = maxDuration;
        }
        
        setPracticeTime(formatTime(displaySeconds));
        setElapsedSeconds(displaySeconds);

        // 检查是否达到最大时长
        if (maxDuration > 0 && currentTotalSeconds >= maxDuration && !isTimeUpTriggeredRef.current) {
          isTimeUpTriggeredRef.current = true;
          if (onTimeUpRef.current) {
            onTimeUpRef.current();
          }
        }
      }, 30000);
      
      return () => {
        clearInterval(displayInterval);
        clearInterval(calibrationInterval);
      };
    } else {
      // 如果没有会话开始时间，重置为00:00
      setPracticeTime('00:00');
      setElapsedSeconds(0);
      isTimeUpTriggeredRef.current = false;
    }
  }, [sessionStartTime, calculatePracticeTime, formatTime, maxDuration]);


  return (
    <motion.div 
      className="bg-gradient-to-r from-purple-100 to-white mx-4 my-4 rounded-2xl"
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <motion.div 
        className="px-4 py-4 rounded-2xl"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3, delay: 0.2 }}
      >
        <div className="flex items-center justify-between">
          <motion.div 
            className="text-gray-800 text-base leading-6 font-medium flex items-center"
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.4, delay: 0.3 }}
          >
            <img className="w-6 h-6" src={logo3} alt="" />
            {/* <span className="ml-3">{t('ai.training.goalAchievement')}</span> */}
          </motion.div>
          <div className="flex items-center space-x-3">
            {/* 练习时长显示 */}
            <motion.div
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: 0.5 }}
            >
              <CountdownTimer maxDuration={maxDuration} elapsedSeconds={elapsedSeconds} />
            </motion.div>
            
            {/* 结束按钮 */}
            <motion.div
              onClick={onFinishClick}
              className="text-sm bg-white font-normal text-purple-600 leading-5 py-1.5 px-4 rounded-full cursor-pointer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              transition={{ duration: 0.2 }}
              title={!isMinDurationMet ? t('ai.training.suggestPracticeDuration', '建议练习至少{minutes}分钟', { minutes: Math.ceil(minDuration / 60) }) : ''}
            >
              {t('ai.training.endPractice')}
            </motion.div>
          </div>
        </div>
        <motion.div 
          className="relative bg-white mt-7 rounded-3xl"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <ProgressBar aiIconPosition={aiIconPosition} />
          <Popover
            content={analysisReason}
            visible={showAnalysisBubble && !!analysisReason}
            onVisibleChange={onToggleAnalysisBubble}
            placement="bottom"
            trigger="click"
            mode="dark"
            getContainer={() => document.body}
            destroyOnHide
          >
            <motion.div
              className="absolute cursor-pointer w-[40px]"
              style={{
                left: aiIconPosition >= 100 ? 'calc(100% - 20px)' : `${aiIconPosition}%`,
                marginTop: '-32px',
              }}
              animate={{ 
                left: aiIconPosition >= 100 ? 'calc(100% - 20px)' : `${aiIconPosition}%`
              }}
              transition={{ duration: 0.5, ease: 'easeInOut' }}
            >
              {aiIconPosition > 60 ? (
                <img
                  style={{ width: '40px', height: '40px' }}
                  src={a2}
                  alt=""
                />
              ) : aiIconPosition < 30 ? (
                <img
                  style={{ width: '40px', height: '40px' }}
                  src={err1}
                  alt=""
                />
              ) : (
                <img
                  style={{ width: '40px', height: '40px' }}
                  src={a1}
                  alt=""
                />
              )}
            </motion.div>
          </Popover>
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

export default TopStatusBar;